import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import Button from "@/components/site/Button";

const tiers = [
  {
    name: "Starter",
    price: "£99",
    per: "/month",
    tag: "For single site operators",
    cta: "Start Free Trial",
    ctaId: "pricing-starter-cta",
    staff: "Up to 10 staff",
    outcome: "Everything you need to prove one site is inspection ready.",
    features: [
      "Unlimited policies and SOP updates",
      "Verified acknowledgements per member of staff",
      "Signed digital certificates on completion",
      "Standard regulator export in CSV and PDF",
      "Immutable audit history retained for 7 years",
    ],
    highlight: false,
  },
  {
    name: "Professional",
    price: "£299",
    per: "/month",
    tag: "For growing regulated teams",
    cta: "Start Free Trial",
    ctaId: "pricing-pro-cta",
    staff: "Up to 30 staff",
    outcome:
      "Prove comprehension, not just receipt. Built for teams that are audited.",
    features: [
      "Everything in Starter",
      "Adaptive comprehension checks per module",
      "Multi site and role based permissions",
      "Compliance velocity analytics per department",
      "Priority support with named response times",
    ],
    highlight: true,
    badge: "Most Popular",
  },
  {
    name: "Enterprise",
    price: "Custom",
    per: "",
    tag: "For groups and manufacturers",
    cta: "Contact Sales",
    ctaId: "pricing-enterprise-cta",
    staff: "Unlimited staff, unlimited sites",
    outcome:
      "The compliance backbone for regulated groups operating at scale.",
    features: [
      "Everything in Professional",
      "SSO with SAML and SCIM directory sync",
      "Dedicated onboarding and regulatory workshops",
      "SLA, DPA and single tenant hosting options",
      "Named customer success manager",
    ],
    highlight: false,
  },
];

function handleTierClick(e, tierName, cta) {
  e.preventDefault();
  const label = cta === "Contact Sales" ? "Enterprise enquiry" : `${tierName} 14 day free trial`;
  const message =
    cta === "Contact Sales"
      ? `Hello, I am interested in the ${tierName} plan for our organisation. Please get in touch to discuss requirements, pricing and onboarding.`
      : `Hello, I would like to start the 14 day free trial on the ${tierName} plan. Please help me get our organisation set up on CanniComply.`;

  // Broadcast so the Contact form can pick up the prefill even if it mounted first.
  window.dispatchEvent(
    new CustomEvent("cannicomply:prefill-contact", {
      detail: { plan: tierName, label, message },
    })
  );

  const target = document.getElementById("contact");
  if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });

  // Focus name field for keyboard users after scroll settles
  setTimeout(() => {
    const nameInput = document.querySelector('[data-testid="contact-name"]');
    if (nameInput) nameInput.focus({ preventScroll: true });
  }, 550);
}

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-14 md:py-24 border-t border-white/[0.04]"
    >
      <div className="absolute inset-0 pointer-events-none grid-lines opacity-40" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="max-w-2xl">
          <div className="eyebrow">Pricing</div>
          <h2 className="mt-3 font-display text-4xl md:text-[44px] tracking-[-0.035em] text-white leading-[1.02] font-semibold">
            Simple, transparent pricing.
          </h2>
          <p className="mt-4 text-slate-400 text-lg leading-relaxed">
            Per organisation pricing. No setup fees. Free 14 day trial on every
            plan. Cancel any time.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 items-stretch">
          {tiers.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.55, delay: i * 0.05 }}
              className={`relative rounded-lg p-7 flex flex-col ${
                t.highlight
                  ? "bg-gradient-to-b from-[#0F1424] to-[#08111F] border border-[#D4A65A]/40 shadow-[0_30px_80px_-25px_rgba(212,166,90,0.25)] md:-translate-y-3"
                  : "bg-[#070d1c] border border-white/[0.06]"
              }`}
            >
              {t.highlight && (
                <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 rounded-full bg-[#D4A65A] text-[#0B1220] px-3 py-1 text-[10px] uppercase tracking-[0.15em] font-semibold">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#0B1220] animate-pulse" />
                  {t.badge}
                </div>
              )}

              <div>
                <div className="text-white font-display text-xl tracking-tight">
                  {t.name}
                </div>
                <div className="text-xs text-slate-500 mt-1">{t.tag}</div>
              </div>

              <div className="mt-6 flex items-baseline gap-1">
                <span className="font-display text-5xl tracking-tighter text-white">
                  {t.price}
                </span>
                {t.per && (
                  <span className="text-slate-500 text-sm">{t.per}</span>
                )}
              </div>

              <div className="mt-3 inline-flex items-center gap-2 self-start rounded-full border border-white/[0.08] bg-white/[0.02] px-3 py-1">
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    t.highlight ? "bg-[#D4A65A]" : "bg-emerald-400/80"
                  }`}
                />
                <span className="text-[11px] text-slate-300 tracking-tight">
                  {t.staff}
                </span>
              </div>

              <p className="mt-4 min-h-[2.75rem] text-[12.5px] text-slate-400 leading-relaxed">
                {t.outcome}
              </p>

              <Button
                as="a"
                href="#contact"
                onClick={(e) => handleTierClick(e, t.name, t.cta)}
                data-testid={t.ctaId}
                variant={t.highlight ? "primary" : "ghost"}
                size="md"
                className="mt-6 w-full"
              >
                {t.highlight && <Sparkles size={13} />}
                {t.cta}
              </Button>

              <ul className="mt-7 space-y-3 flex-1">
                {t.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-2.5 text-[13px] text-slate-300 leading-snug"
                  >
                    <span className="mt-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500/[0.10] border border-emerald-500/30 shrink-0">
                      <Check size={10} className="text-emerald-400" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[11px] uppercase tracking-[0.22em] text-slate-500">
          <span className="inline-flex items-center gap-2">
            <span className="h-1 w-1 rounded-full bg-emerald-400" />
            14 day free trial
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="h-1 w-1 rounded-full bg-emerald-400" />
            No setup fees
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="h-1 w-1 rounded-full bg-emerald-400" />
            Cancel any time
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="h-1 w-1 rounded-full bg-emerald-400" />
            UK region hosted
          </span>
        </div>
      </div>
    </section>
  );
}
